import { EnvelopeClosedIcon } from "@radix-ui/react-icons";
import { Button, Card, Container, Flex, Heading, Text } from "@radix-ui/themes";
import { useEffect, useRef, useState } from "react";
import logo from "../../assets/logo.png";
import { usePostHog } from "../../hooks/usePostHog";
import Alert from "../shared/Alert";
import Footer from "../shared/Footer";

interface CheckEmailViewProps {
    email: string;
    onBackToSignIn?: () => void;
}

const CheckEmailView = ({ email, onBackToSignIn }: CheckEmailViewProps) => {
    const { track } = usePostHog();
    const [resending, setResending] = useState(false);
    const [msg, setMsg] = useState<{
        type: "success" | "error";
        text: string;
    } | null>(null);
    const hasTrackedView = useRef(false);

    useEffect(() => {
        if (hasTrackedView.current) {
            return;
        }
        hasTrackedView.current = true;
        track("page_check_email_viewed", {
            email,
        });
    }, [
        track,
        email,
    ]);

    const handleResend = async () => {
        setResending(true);
        setMsg(null);

        try {
            const response = await fetch("/auth/backend/resend-signup-confirmation", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: "include",
                body: JSON.stringify({
                    email,
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error_description || "Failed to resend confirmation email");
            }

            track("auth_resend_confirmation_completed", {
                email,
            });
            setMsg({
                type: "success",
                text: data.message || "Confirmation email sent.",
            });
        } catch (err: any) {
            const errorMessage = err.message || "Network error. Please try again.";
            track("auth_resend_confirmation_failed", {
                email,
                error_message: errorMessage,
            });
            setMsg({
                type: "error",
                text: errorMessage,
            });
        } finally {
            setResending(false);
        }
    };

    return (
        <Container
            size="2"
            p="4"
            style={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                // Optical centering — consistent with the login and consent screens.
                paddingBottom: "12vh",
            }}
        >
            <Card
                size="4"
                style={{
                    width: "100%",
                }}
            >
                <Flex
                    direction="column"
                    gap="4"
                    align="center"
                    style={{
                        textAlign: "center",
                        marginBottom: "24px",
                    }}
                >
                    <img
                        src={logo}
                        alt="Logo"
                        style={{
                            width: 64,
                            height: 64,
                            objectFit: "contain",
                        }}
                    />
                    <Flex align="center" gap="2" justify="center">
                        <EnvelopeClosedIcon width="24" height="24" />
                        <Heading size="6">Confirm your email</Heading>
                    </Flex>
                    <Text color="gray" size="2">
                        We sent a confirmation link to
                    </Text>
                    <Text size="3" weight="bold">
                        {email}
                    </Text>
                </Flex>

                <Flex direction="column" gap="3">
                    {msg && <Alert type={msg.type} message={msg.text} onClose={() => setMsg(null)} />}

                    <Text
                        as="p"
                        size="2"
                        color="gray"
                        style={{
                            textAlign: "center",
                        }}
                    >
                        Open it to finish signing up. Didn't get it? Check spam, or resend below.
                    </Text>

                    <Button
                        size="3"
                        variant="soft"
                        onClick={handleResend}
                        loading={resending}
                        style={{
                            cursor: "pointer",
                        }}
                    >
                        Resend confirmation email
                    </Button>

                    {onBackToSignIn && (
                        <Button
                            size="3"
                            variant="ghost"
                            onClick={onBackToSignIn}
                            style={{
                                cursor: "pointer",
                            }}
                            disabled={resending}
                        >
                            Back to Sign In
                        </Button>
                    )}
                </Flex>
            </Card>
            <Footer />
        </Container>
    );
};

export default CheckEmailView;
